const escape = value => String(value ?? '').replace(/[&<>"']/g, char => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[char]));
const pending = () => ({status: 'pending', references: []});

/** Follow resolved scan edges only; unrelated drawings and cycles stay out. */
export function packageScope(pkg, source) {
  const scans = pkg.scans || {};
  const models = new Set((pkg.files || []).filter(file => file.model).map(file => file.path));
  const visited = new Set();
  const queue = [{path: source, chain: [source]}];
  const result = [];
  for (let index = 0; index < queue.length; index++) {
    const entry = queue[index];
    if (visited.has(entry.path)) continue;
    visited.add(entry.path);
    const scan = scans[entry.path] || pending();
    result.push({...entry, scan});
    for (const ref of scan.references || []) {
      if (ref.kind === 'font' || ref.status !== 'found' || !ref.target) continue;
      if (!Object.hasOwn(scans, ref.target) && !models.has(ref.target)) continue;
      queue.push({path: ref.target, chain: [...entry.chain, ref.target]});
    }
  }
  return result;
}

const labels = {found: '套件內找到', missing: '套件未包含', ambiguous: '同名檔案需確認'};
const kinds = {xref: '外參', font: '字型', image: '圖片', underlay: '底圖', link: '連結'};
function reference(ref) {
  const status = ref.reason === 'name-mismatch'
    ? `檔名不同，請確認對應：${(ref.suggestions || []).join('、')}`
    : `${ref.kind === 'font' && ref.method === 'system' ? '系統字型' : labels[ref.status] || '待確認'}${ref.target ? `：${ref.target}` : ''}`;
  return `<li>${escape(kinds[ref.kind] || ref.kind)} · ${escape(ref.path)} — ${escape(status)}</li>`;
}

function scanSection({path, chain, scan}) {
  const title = `<h4>${escape(path)}</h4>${chain.length > 1 ? `<p class="package-chain">引用鏈：${chain.map(escape).join(' → ')}</p>` : ''}`;
  if (scan.status === 'pending' || !scan.status) return `<section>${title}<p>掃描中，尚無完整結果。</p></section>`;
  if (scan.status === 'error') return `<section>${title}<p>掃描失敗：${escape(scan.error || '原因未記錄')}</p></section>`;
  const refs = scan.references || [];
  const drawingRefs = refs.filter(ref => ref.kind !== 'font');
  const unresolved = drawingRefs.filter(ref => ref.status !== 'found');
  const found = drawingRefs.filter(ref => ref.status === 'found');
  const fonts = refs.filter(ref => ref.kind === 'font').sort((a, b) => Number(a.status === 'found') - Number(b.status === 'found'));
  return `<section>${title}${unresolved.length ? `<h5>待確認外參（${unresolved.length}）</h5><ul>${unresolved.map(reference).join('')}</ul>` : ''}
    ${found.length ? `<h5>已找到的外參（${found.length}）</h5><ul>${found.map(reference).join('')}</ul>` : ''}
    ${!drawingRefs.length ? '<p>未找到外參、圖片或底圖引用。</p>' : ''}
    ${fonts.length ? `<details class="package-fonts"><summary>字型（${fonts.length}）</summary><ul>${fonts.map(reference).join('')}</ul></details>` : ''}</section>`;
}

export function renderPackageReport(host, pkg, item, fmtBytes) {
  const source = item.package?.path;
  const scope = source ? packageScope(pkg, source) : [];
  host.innerHTML = `<p>目前檔案：<strong>${escape(source || item.name)}</strong></p>
    <p>預設只列本檔及其下層引用。套件內找到檔案不代表引擎已成功載入；字型另列。</p>
    <h3>本檔外參掃描</h3>${scope.length ? scanSection(scope[0]) : '<p>未記錄套件內路徑，請查看完整套件。</p>'}
    ${scope.length > 1 ? `<h3>相關下層外參（${scope.length - 1}）</h3>${scope.slice(1).map(scanSection).join('')}` : ''}
    <details class="package-full"><summary>查看完整套件（${pkg.files.length} 個檔案）</summary><div class="package-full-content"></div></details>
    <a class="btn" href="/api/library/${encodeURIComponent(item.id)}/package?download=1">下載原始 ZIP</a>`;
  const full = host.querySelector('.package-full');
  full.addEventListener('toggle', () => {
    if (!full.open || full.dataset.loaded) return;
    full.querySelector('.package-full-content').innerHTML = fullPackageHtml(pkg, fmtBytes);
    full.dataset.loaded = '1';
  });
}

function fullPackageHtml(pkg, fmtBytes) {
    const scans = Object.entries(pkg.scans || {}).map(([path, scan]) => scanSection({path, chain: [path], scan: scan || pending()})).join('');
    const rows = pkg.files.map(file => {
      const scan = pkg.scans?.[file.path];
      const role = scan?.role === 'reference' ? '外參圖面' : scan?.role === 'root' ? '主圖候選' : file.model ? '圖面' : '附件';
      return `<tr><td>${escape(file.path)}</td><td>${role}</td><td>${escape(fmtBytes(file.size))}</td></tr>`;
    }).join('');
    return `<p>${pkg.files.length} 個檔案 · 解壓 ${escape(fmtBytes(pkg.bytes))}</p><h3>完整外參掃描</h3>${scans || '<p>尚無掃描結果。</p>'}<h3>完整檔案清單</h3><table><thead><tr><th>套件路徑</th><th>用途</th><th>大小</th></tr></thead><tbody>${rows}</tbody></table>`;
}

export function renderOriginalPackage(host, pkg, item, fmtBytes) {
  host.innerHTML = fullPackageHtml(pkg, fmtBytes) + `<a class="btn" href="/api/library/${encodeURIComponent(item.id)}/package?download=1">下載原始 ZIP</a>`;
}
